import { useEffect, useMemo, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Loader2,
  LogOut,
  Search,
  RefreshCw,
  Download,
  Mail,
  Package,
  Inbox,
  FileText,
  Copy,
  ChevronDown,
} from "lucide-react";
import { useAdminAuth } from "@/lib/adminAuth";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";

export const ADMIN_DASH_TEST_IDS = {
  page: "admin-dashboard",
  logout: "admin-logout",
  refresh: "admin-refresh",
  search: "admin-search",
  filterType: "admin-filter-type",
  filterTopic: "admin-filter-topic",
  exportCsv: "admin-export-csv",
  row: (id) => `admin-row-${id}`,
  rowExpand: (id) => `admin-row-expand-${id}`,
  count: "admin-count",
};

function normalize(contacts, quotes) {
  const rows = [];
  for (const c of contacts || []) {
    rows.push({
      id: c.id,
      kind: "contact",
      created_at: c.created_at,
      name: c.name,
      email: c.email || "",
      business: c.business_name || "",
      topic: c.topic || "",
      summary: c.message,
      raw: c,
    });
  }
  for (const q of quotes || []) {
    rows.push({
      id: q.id,
      kind: "quote",
      created_at: q.created_at,
      name: q.name,
      email: q.email || "",
      business: q.business_name || "",
      topic: q.package_type || "",
      summary: `${q.pickup_address} → ${q.dropoff_address}`,
      raw: q,
    });
  }
  rows.sort((a, b) => (a.created_at < b.created_at ? 1 : -1));
  return rows;
}

function fmtDate(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

function csvEscape(v) {
  if (v == null) return "";
  const s = String(v).replace(/"/g, '""');
  return /[",\n]/.test(s) ? `"${s}"` : s;
}

function toCSV(rows) {
  const header = [
    "type",
    "created_at",
    "name",
    "email",
    "business",
    "topic",
    "summary",
    "phone",
    "pickup",
    "dropoff",
    "weight_lbs",
    "requested_date",
    "time_window",
    "frequency",
    "notes",
  ];
  const lines = [header.join(",")];
  for (const r of rows) {
    const raw = r.raw || {};
    lines.push(
      [
        r.kind,
        r.created_at,
        r.name,
        r.email,
        r.business,
        r.topic,
        r.summary,
        raw.phone || "",
        raw.pickup_address || "",
        raw.dropoff_address || "",
        raw.weight_lbs ?? "",
        raw.requested_date || "",
        raw.time_window || "",
        raw.frequency || "",
        raw.notes || "",
      ]
        .map(csvEscape)
        .join(",")
    );
  }
  return lines.join("\n");
}

function KindBadge({ kind }) {
  const isContact = kind === "contact";
  return (
    <span
      className={`inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] px-2 py-1 border ${
        isContact
          ? "border-forest/25 text-forest bg-cream"
          : "border-terracotta/40 text-terracotta bg-terracotta/5"
      }`}
    >
      {isContact ? <Mail className="w-3 h-3" /> : <Package className="w-3 h-3" />}
      {isContact ? "Contact" : "Quote"}
    </span>
  );
}

function DetailRow({ label, value }) {
  if (!value && value !== 0) return null;
  return (
    <div className="grid grid-cols-12 gap-4 py-2 border-t border-forest/10 first:border-t-0">
      <div className="col-span-4 md:col-span-3 text-[10px] uppercase tracking-[0.25em] text-charcoal/60 pt-1">
        {label}
      </div>
      <div className="col-span-8 md:col-span-9 text-sm text-forest whitespace-pre-wrap break-words">
        {String(value)}
      </div>
    </div>
  );
}

function RowDetail({ row }) {
  const r = row.raw;
  if (row.kind === "contact") {
    return (
      <div className="p-4 md:p-6 bg-cream border-t border-forest/15">
        <DetailRow label="Name" value={r.name} />
        <DetailRow label="Business" value={r.business_name} />
        <DetailRow label="Email" value={r.email} />
        <DetailRow label="Topic" value={r.topic} />
        <DetailRow label="Message" value={r.message} />
        <DetailRow label="Received" value={fmtDate(r.created_at)} />
      </div>
    );
  }
  return (
    <div className="p-4 md:p-6 bg-cream border-t border-forest/15">
      <DetailRow label="Name" value={r.name} />
      <DetailRow label="Business" value={r.business_name} />
      <DetailRow label="Email" value={r.email} />
      <DetailRow label="Phone" value={r.phone} />
      <DetailRow label="Pickup" value={r.pickup_address} />
      <DetailRow label="Dropoff" value={r.dropoff_address} />
      <DetailRow label="Package" value={r.package_type} />
      <DetailRow label="Weight" value={r.weight_lbs ? `${r.weight_lbs} lbs` : null} />
      <DetailRow label="Requested date" value={r.requested_date} />
      <DetailRow label="Time window" value={r.time_window} />
      <DetailRow label="Frequency" value={r.frequency} />
      <DetailRow label="Notes" value={r.notes} />
      <DetailRow label="Received" value={fmtDate(r.created_at)} />
    </div>
  );
}

export default function AdminDashboardPage() {
  const { admin, ready, logout, authedGet } = useAdminAuth();
  const navigate = useNavigate();
  const [data, setData] = useState({ contacts: [], quotes: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [q, setQ] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [topicFilter, setTopicFilter] = useState("all");
  const [expanded, setExpanded] = useState(() => new Set());

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await authedGet("/admin/submissions");
      setData(res);
    } catch (e) {
      setError("Couldn't load submissions.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (ready && admin) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, admin]);

  const rows = useMemo(() => normalize(data.contacts, data.quotes), [data]);

  const topics = useMemo(() => {
    const s = new Set();
    for (const r of rows) if (r.topic) s.add(r.topic);
    return Array.from(s).sort();
  }, [rows]);

  const filtered = useMemo(() => {
    const query = q.trim().toLowerCase();
    return rows.filter((r) => {
      if (typeFilter !== "all" && r.kind !== typeFilter) return false;
      if (topicFilter !== "all" && r.topic !== topicFilter) return false;
      if (!query) return true;
      const hay = `${r.name} ${r.email} ${r.business} ${r.topic} ${r.summary}`.toLowerCase();
      return hay.includes(query);
    });
  }, [rows, q, typeFilter, topicFilter]);

  if (!ready) {
    return (
      <main className="min-h-[80vh] flex items-center justify-center text-charcoal/60">
        <Loader2 className="w-5 h-5 animate-spin" />
      </main>
    );
  }
  if (!admin) return <Navigate to="/admin/login" replace />;

  const toggle = (id) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const doExport = () => {
    const csv = toCSV(filtered);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `waylio-submissions-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    toast.success("CSV exported.");
  };

  const copyEmail = (email) => {
    if (!email) return;
    navigator.clipboard?.writeText(email);
    toast.success(`Copied ${email}`);
  };

  return (
    <main data-testid={ADMIN_DASH_TEST_IDS.page} className="min-h-[100vh] bg-paper pt-24 md:pt-28 pb-24">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <div className="text-xs uppercase tracking-[0.3em] text-terracotta mb-2">
              Waylio · Dispatch
            </div>
            <h1 className="font-serif text-4xl md:text-6xl leading-[1.02] tracking-tight text-forest">
              Submissions
            </h1>
            <p className="mt-2 text-sm text-charcoal/60">
              Signed in as <span className="text-forest">{admin.email}</span>
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <button
              data-testid={ADMIN_DASH_TEST_IDS.refresh}
              onClick={load}
              className="inline-flex items-center gap-2 border border-forest/25 text-forest px-4 py-2.5 text-sm hover:bg-forest hover:text-cream transition-colors"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Refresh
            </button>
            <button
              data-testid={ADMIN_DASH_TEST_IDS.exportCsv}
              onClick={doExport}
              className="inline-flex items-center gap-2 bg-forest text-cream px-4 py-2.5 text-sm hover:bg-terracotta transition-colors"
            >
              <Download className="w-4 h-4" /> Export CSV
            </button>
            <button
              data-testid={ADMIN_DASH_TEST_IDS.logout}
              onClick={() => {
                logout();
                navigate("/admin/login");
              }}
              className="inline-flex items-center gap-2 text-charcoal/70 hover:text-terracotta text-sm px-3 py-2"
            >
              <LogOut className="w-4 h-4" /> Sign out
            </button>
          </div>
        </div>

        {/* Stats */}
        <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-3">
          <StatCard label="Total" value={rows.length} Icon={Inbox} />
          <StatCard label="Contacts" value={data.contacts?.length || 0} Icon={Mail} />
          <StatCard label="Quotes" value={data.quotes?.length || 0} Icon={Package} />
          <StatCard label="Showing" value={filtered.length} Icon={FileText} testId={ADMIN_DASH_TEST_IDS.count} />
        </div>

        {/* Filters */}
        <div className="mt-8 grid grid-cols-12 gap-3 md:gap-4">
          <div className="col-span-12 md:col-span-6 flex items-stretch gap-2 border-b border-forest/25 focus-within:border-terracotta">
            <Search className="w-4 h-4 text-charcoal/50 mt-3 shrink-0" />
            <input
              data-testid={ADMIN_DASH_TEST_IDS.search}
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search name, email, business, message…"
              className="flex-1 bg-transparent outline-none py-2 text-forest placeholder:text-charcoal/40"
            />
          </div>
          <div className="col-span-6 md:col-span-3">
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger
                data-testid={ADMIN_DASH_TEST_IDS.filterType}
                className="w-full bg-transparent border-0 border-b border-forest/30 rounded-none px-0 h-10 focus:ring-0 text-forest"
              >
                <SelectValue placeholder="Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All types</SelectItem>
                <SelectItem value="contact">Contacts</SelectItem>
                <SelectItem value="quote">Quotes</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="col-span-6 md:col-span-3">
            <Select value={topicFilter} onValueChange={setTopicFilter}>
              <SelectTrigger
                data-testid={ADMIN_DASH_TEST_IDS.filterTopic}
                className="w-full bg-transparent border-0 border-b border-forest/30 rounded-none px-0 h-10 focus:ring-0 text-forest"
              >
                <SelectValue placeholder="Topic" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All topics</SelectItem>
                {topics.map((t) => (
                  <SelectItem key={t} value={t}>{t}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Table */}
        <div className="mt-8 border-t border-forest/15">
          {loading && (
            <div className="py-16 flex items-center justify-center text-charcoal/60">
              <Loader2 className="w-5 h-5 animate-spin" />
            </div>
          )}
          {!loading && error && (
            <div className="py-8 text-terracotta">{error}</div>
          )}
          {!loading && !error && filtered.length === 0 && (
            <div className="py-20 text-center text-charcoal/60">No submissions match your filters.</div>
          )}

          {!loading && filtered.map((r, i) => {
            const isOpen = expanded.has(r.id);
            return (
              <motion.div
                key={r.id}
                data-testid={ADMIN_DASH_TEST_IDS.row(r.id)}
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: Math.min(i * 0.015, 0.3) }}
                className="border-b border-forest/10"
              >
                <button
                  data-testid={ADMIN_DASH_TEST_IDS.rowExpand(r.id)}
                  onClick={() => toggle(r.id)}
                  className="w-full text-left grid grid-cols-12 gap-2 md:gap-4 items-center py-4 md:py-5 hover:bg-cream/50 transition-colors px-2"
                >
                  <div className="col-span-6 md:col-span-3">
                    <div className="flex items-center gap-2">
                      <KindBadge kind={r.kind} />
                    </div>
                    <div className="mt-2 font-serif text-lg text-forest leading-tight">
                      {r.name}
                    </div>
                    {r.business && (
                      <div className="text-xs text-charcoal/60">{r.business}</div>
                    )}
                  </div>
                  <div className="col-span-6 md:col-span-3 text-sm">
                    <div className="text-charcoal/50 text-[10px] uppercase tracking-[0.25em] mb-1">
                      {r.kind === "quote" ? "Package" : "Topic"}
                    </div>
                    <div className="text-forest">{r.topic || "—"}</div>
                  </div>
                  <div className="col-span-9 md:col-span-4">
                    <div className="text-charcoal/50 text-[10px] uppercase tracking-[0.25em] mb-1">
                      Summary
                    </div>
                    <div className="text-forest/90 text-sm line-clamp-2">
                      {r.summary}
                    </div>
                  </div>
                  <div className="col-span-3 md:col-span-2 text-right md:text-left">
                    <div className="text-charcoal/50 text-[10px] uppercase tracking-[0.25em] mb-1">
                      Received
                    </div>
                    <div className="text-sm text-forest">{fmtDate(r.created_at)}</div>
                    <div className="mt-1 flex items-center gap-2 justify-end md:justify-start text-xs text-charcoal/60">
                      {r.email && (
                        <span
                          role="button"
                          tabIndex={0}
                          onClick={(e) => {
                            e.stopPropagation();
                            copyEmail(r.email);
                          }}
                          className="inline-flex items-center gap-1 hover:text-terracotta"
                          aria-label="Copy email"
                        >
                          <Copy className="w-3 h-3" />
                          <span className="truncate max-w-[110px]">{r.email}</span>
                        </span>
                      )}
                      <ChevronDown
                        className={`w-4 h-4 transition-transform ${isOpen ? "rotate-180" : ""}`}
                      />
                    </div>
                  </div>
                </button>
                {isOpen && <RowDetail row={r} />}
              </motion.div>
            );
          })}
        </div>
      </div>
    </main>
  );
}

function StatCard({ label, value, Icon, testId }) {
  return (
    <div className="border border-forest/15 bg-cream p-4 md:p-5">
      <div className="flex items-center justify-between text-charcoal/60">
        <span className="text-[10px] uppercase tracking-[0.3em]">{label}</span>
        <Icon className="w-4 h-4 text-forest" strokeWidth={1.5} />
      </div>
      <div data-testid={testId} className="mt-3 font-serif text-3xl md:text-4xl text-forest">
        {value}
      </div>
    </div>
  );
}
